import { AppContext } from '../contexts';
import { useEffect, useState } from 'react';
import { get } from '../api';
import { useAuth, useSnack } from '../hooks';
import { useTranslation } from 'react-i18next';
import { RoleEnum } from '@koob/enums';
import { actingOrganizationOf } from '@koob/utils';

const permissions = {
  inviteUser: [RoleEnum.TO_ADMIN],
  readSelfOrganization: [
    RoleEnum.TO_ADMIN,
    RoleEnum.TO_USER,
    RoleEnum.DMC_USER,
  ],
  manageSelfOrganization: [RoleEnum.TO_ADMIN],
  manageSelfOrganizationUsers: [RoleEnum.TO_ADMIN],
  readTravelDiaryContent: [RoleEnum.TO_ADMIN, RoleEnum.KOOB_ADMIN],
  manageTravelDiaryContent: [RoleEnum.TO_ADMIN, RoleEnum.KOOB_ADMIN],
  readAuditLog: [RoleEnum.TO_ADMIN, RoleEnum.KOOB_ADMIN],
};

const AppProvider = ({ children }) => {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const { notify } = useSnack();

  const [user, setUser] = useState(null);

  useEffect(() => {
    get('/api/v1/user')
      .then(r => setUser(r.data))
      // An expired session reloads to the login screen (api interceptor).
      .catch(() => {});
  }, []);

  const currentUserRole = user?.role;

  if (
    currentUserRole &&
    (currentUserRole === RoleEnum.DMC_ADMIN ||
      currentUserRole === RoleEnum.DMC_USER)
  ) {
    logout();
    notify(t('errors:userNotAuthorized'), { type: 'error' });
  }

  const canManageUser = targetUser => {
    if (currentUserRole === RoleEnum.TO_ADMIN) return true;

    if (targetUser?.id === user?.id) {
      return false;
    }

    return false;
  };

  const isAllowedTo = name =>
    (permissions[name] || []).indexOf(currentUserRole) !== -1;

  // The organization this tab acts as (KD-3735 picker), else the first.
  const currentOrganization = actingOrganizationOf(user);
  const currentOrganizationPosition = {
    lat: parseFloat(currentOrganization?.lat),
    lon: parseFloat(currentOrganization?.lon),
  };

  return (
    <AppContext.Provider
      value={{
        user: user,
        currentUserRole,
        currentOrganizationId: currentOrganization?.id,
        currentOrganization,
        currentOrganizationIdInt: currentOrganization?.organizationId,
        currentUserLang: currentOrganization?.language,
        currentOrganizationPosition,
        canManageUser,
        isAllowedTo,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
